import { useLocation } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import { useContentBundle } from '@/hooks/useContentBundle'
import type { FeedbackContext, GetContentBundleResponse } from '@/types/api'

const OVERLAY_CONTEXTS: Record<string, string> = {
  overview: 'Overview',
  appendices: 'Appendices',
  glossary: 'Glossary',
  'release-notes': 'ReleaseNotes',
  'user-content': 'UserContent',
  faq: 'Faq',
  crosswalks: 'Crosswalks',
}

/** Resolve what is on screen from the route, falling back to Home. */
function resolveContent(
  pathname: string,
  search: string,
  bundle: GetContentBundleResponse | undefined
): { contentContext: string; entityId?: number; contentLabel?: string; contentVersion?: string; wasDraft?: boolean } {
  const practiceArea = pathname.match(/^\/practice-area\/(\d+)/)
  if (practiceArea) {
    const id = Number(practiceArea[1])
    const entity = bundle?.practiceAreas.find((pa) => pa.id === id)
    return {
      contentContext: 'PracticeArea',
      entityId: id,
      contentLabel: entity?.displayName,
      contentVersion: versionOf(entity),
      wasDraft: entity?.isDraft,
    }
  }

  const practice = pathname.match(/^\/practice\/(\d+)/)
  if (practice) {
    const id = Number(practice[1])
    const entity = bundle?.practices.find((p) => p.id === id)
    return {
      contentContext: 'Practice',
      entityId: id,
      contentLabel: entity?.displayName,
      contentVersion: versionOf(entity),
      wasDraft: entity?.isDraft,
    }
  }

  // External frameworks are language-neutral reference data, so there is no translation version.
  const framework = pathname.match(/^\/framework\/(\d+)/)
  if (framework) {
    const id = Number(framework[1])
    return {
      contentContext: 'Framework',
      entityId: id,
      contentLabel: bundle?.externalFrameworks.find((f) => f.id === id)?.name,
    }
  }

  if (pathname.startsWith('/search')) {
    return {
      contentContext: 'Search',
      contentLabel: new URLSearchParams(search).get('q') ?? undefined,
    }
  }

  return { contentContext: 'Home' }
}

/**
 * The translation version the entity was rendered from. Undefined for a bundle cached before the
 * field existed, so an out-of-date offline client reports no version rather than a wrong one.
 */
function versionOf(entity: { version?: number } | undefined): string | undefined {
  return typeof entity?.version === 'number' ? String(entity.version) : undefined
}

/**
 * Builds the context snapshot attached to a feedback submission: language, what is displayed, the
 * translation version of that entity, whether it was a draft overlay, offline state and the
 * active model view. Read at submit time, not at
 * dialog-open time, so a user who navigates with the dialog open reports where they ended up.
 */
export function useFeedbackContext(): () => FeedbackContext {
  const { pathname, search } = useLocation()
  const { data: bundle } = useContentBundle()

  return () => {
    const {
      selectedLanguage,
      isOffline,
      selectedModelViewId,
      selectedCustomViewId,
      customViews,
      overlayPage,
    } = useAppStore.getState()

    // A page overlay sits on top of the routed content, so it wins when open.
    const content = overlayPage
      ? { contentContext: OVERLAY_CONTEXTS[overlayPage.type] ?? overlayPage.type, entityId: undefined, contentLabel: undefined, contentVersion: undefined, wasDraft: undefined }
      : resolveContent(pathname, search, bundle)

    const customView = selectedCustomViewId !== null
      ? customViews.find((cv) => cv.id === selectedCustomViewId)
      : undefined
    const modelView = selectedModelViewId !== null
      ? bundle?.modelViews.find((mv) => mv.id === selectedModelViewId)
      : undefined

    return {
      languageCode: selectedLanguage || 'en',
      contentContext: content.contentContext,
      entityId: content.entityId,
      contentLabel: content.contentLabel,
      contentVersion: content.contentVersion,
      // Draft preview is per-entity: the bundle marks each item it overlaid with a draft.
      wasDraft: content.wasDraft ?? false,
      wasOffline: isOffline,
      // Custom views are a separate concept from model views. Record the name only, so the id
      // column never holds something that is not a ModelView id.
      modelViewId: customView ? undefined : modelView?.id,
      modelViewName: customView ? `Custom view: ${customView.name}` : (modelView?.displayName ?? 'All Model Views'),
    }
  }
}
